import type { Rarity } from '../core/types';

/**
 * Vignettes de dés **facettées** en SVG (refonte F1, lot 2) : boutique (cartes
 * de machine), révélation de tirage, onglet Dés, Gobelet.
 *
 * Même matière que les dés 3D low-poly : corps en **os** (quatre tons de la
 * facette la plus éclairée à la plus sombre), chiffres à l'**encre**, liseré des
 * arêtes à la **couleur de rareté** (A45, R1 « reflet teinté » : le Commun reste
 * gris). Le Prismatique prend un dégradé irisé, comme dans la maquette F1.
 * d4 (tétraèdre vu de face), d6 (cube de trois quarts), d12 (dodécaèdre vu de
 * dessus), d20 (icosaèdre de trois quarts, v0.26.0), d100 (boule à facettes
 * vue de trois quarts, v0.43.0) ; toute autre face donne un polygone régulier
 * numéroté (repli).
 *
 * Choix assumé (contrat du lot 2) : une vignette **SVG** plutôt qu'un rendu 3D
 * de l'atlas des dés — légère, nette à toute taille, sans contexte WebGL.
 *
 * Module **pur** : renvoie une chaîne SVG (testée dans `tests/ui/diceArt.test.ts`).
 */

const BONE = ['#fbf3df', '#f4e8cc', '#e2d1aa', '#c9b58a'] as const;
const INK = '#3b2a4c';
const LOCKED_EDGE = '#6d6577';
/** Arêtes du dé Basique (sans rareté) : os foncé. */
const PLAIN_EDGE = '#8a7a5c';

/** Couleurs des arêtes par rareté (mêmes valeurs que `--rarity-*`). */
export const DIE_EDGE: Record<Rarity, string> = {
  common: '#9a9aa8',
  uncommon: '#5fbf4a',
  rare: '#3e7bd6',
  epic: '#a565e6',
  legendary: '#e0b53a',
  prismatic: '#22d3ee',
};

export interface DieArtOptions {
  /** Côté en px (largeur = hauteur). */
  size?: number;
  /** Épaisseur du liseré, en unités du `viewBox` 100 × 100. */
  stroke?: number;
  /** Machine verrouillée : arêtes grises, la CSS désature le reste. */
  locked?: boolean;
  /** Dé Basique : pas de rareté, arêtes d'os. */
  plain?: boolean;
  /** Classe(s) CSS en plus de `die-art`. */
  className?: string;
}

type Pt = readonly [number, number];

let gradientSeq = 0;

function pts(points: readonly Pt[]): string {
  return points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
}

function face(points: readonly Pt[], fill: string, edge: string, sw: number, num?: string, at?: Pt, fs = 14): string {
  const poly = `<polygon points="${pts(points)}" fill="${fill}" stroke="${edge}" stroke-width="${sw}" stroke-linejoin="round"/>`;
  if (!num || !at) return poly;
  return (
    poly +
    `<text x="${at[0].toFixed(1)}" y="${at[1].toFixed(1)}" font-size="${fs}" text-anchor="middle" ` +
    `dominant-baseline="central" fill="${INK}" font-family="Cinzel, Georgia, serif" font-weight="700">${num}</text>`
  );
}

function d4(edge: string, sw: number): string {
  const A: Pt = [50, 7];
  const B: Pt = [7, 88];
  const C: Pt = [93, 88];
  const D: Pt = [56, 60];
  return (
    face([A, B, D], BONE[0], edge, sw, '4', [39, 52], 17) +
    face([A, D, C], BONE[2], edge, sw, '2', [66, 55], 15) +
    face([B, C, D], BONE[3], edge, sw, '1', [52, 78], 13)
  );
}

function d6(edge: string, sw: number): string {
  const T: Pt = [50, 6];
  const R: Pt = [91, 29];
  const M: Pt = [50, 52];
  const L: Pt = [9, 29];
  const BL: Pt = [9, 72];
  const BB: Pt = [50, 95];
  const BR: Pt = [91, 72];
  return (
    face([T, R, M, L], BONE[0], edge, sw, '6', [50, 29], 17) +
    face([L, M, BB, BL], BONE[2], edge, sw, '2', [30, 62], 16) +
    face([M, R, BR, BB], BONE[3], edge, sw, '3', [70, 62], 16)
  );
}

function d12(edge: string, sw: number): string {
  const cx = 50;
  const cy = 51;
  const P: Pt[] = [];
  const Q: Pt[] = [];
  const S: Pt[] = [];
  const O: Pt[] = [];
  for (let i = 0; i < 5; i++) {
    const a = ((-90 + 72 * i) * Math.PI) / 180;
    const b = ((-90 + 72 * i + 36) * Math.PI) / 180;
    P.push([cx + 17 * Math.cos(a), cy + 17 * Math.sin(a)]);
    Q.push([cx + 30 * Math.cos(a), cy + 30 * Math.sin(a)]);
    S.push([cx + 44 * Math.cos(b), cy + 44 * Math.sin(b)]);
    O.push([cx + 44 * Math.cos(a), cy + 44 * Math.sin(a)]);
  }
  const shade = [BONE[1], BONE[2], BONE[3], BONE[3], BONE[1]];
  const nums = ['7', '3', '11', '5', '9'];
  let g = '';
  for (let i = 0; i < 5; i++) {
    const j = (i + 4) % 5;
    g += face([Q[i], S[j], O[i], S[i]], BONE[3], edge, sw * 0.8);
  }
  for (let i = 0; i < 5; i++) {
    const k = (i + 1) % 5;
    const mx = (P[i][0] + P[k][0] + Q[i][0] + Q[k][0] + S[i][0]) / 5;
    const my = (P[i][1] + P[k][1] + Q[i][1] + Q[k][1] + S[i][1]) / 5;
    g += face([P[i], P[k], Q[k], S[i], Q[i]], shade[i], edge, sw, nums[i], [mx, my], 9);
  }
  g += face(P, BONE[0], edge, sw, '12', [cx, cy + 1], 12);
  return g;
}

/**
 * **d20** (v0.26.0) : un vrai icosaèdre projeté de trois quarts — la face 20
 * vers l'œil, inclinée vers l'arrière et tournée —, ses dix faces visibles
 * ombrées comme les autres vignettes (lumière en haut à gauche, du ton le
 * plus clair au plus sombre), chiffres sur la face 20 et ses trois voisines
 * (2, 8 et 14 sur un vrai d20). Calculé une fois.
 */
interface D20Face {
  readonly pts: readonly Pt[];
  readonly shade: number;
  readonly label: string | null;
  readonly at: Pt;
  readonly fs: number;
}

/** Pose de la vignette d20 : rotation dans le plan, inclinaison, lacet (degrés). */
const D20_SPIN = 0;
const D20_TILT = 20;
const D20_TURN = 10;

let d20Cache: readonly D20Face[] | null = null;

function d20Faces(): readonly D20Face[] {
  if (d20Cache) return d20Cache;
  type V = [number, number, number];
  const phi = (1 + Math.sqrt(5)) / 2;
  const verts: V[] = [];
  for (const a of [-1, 1]) {
    for (const b of [-1, 1]) verts.push([0, a, b * phi], [a, b * phi, 0], [a * phi, 0, b]);
  }
  const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
  const dot = (a: V, b: V): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const cross = (a: V, b: V): V => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const unit = (a: V): V => {
    const l = Math.hypot(a[0], a[1], a[2]);
    return [a[0] / l, a[1] / l, a[2] / l];
  };
  // Les vingt triangles : trois sommets deux à deux à distance 2 (l'arête).
  const near = (i: number, j: number): boolean => Math.abs(Math.hypot(...sub(verts[i], verts[j])) - 2) < 1e-6;
  const tris: [number, number, number][] = [];
  for (let i = 0; i < 12; i++)
    for (let j = i + 1; j < 12; j++)
      for (let k = j + 1; k < 12; k++) if (near(i, j) && near(j, k) && near(i, k)) tris.push([i, j, k]);
  const rot = (v: V, axis: V, ang: number): V => {
    const c = Math.cos(ang);
    const sn = Math.sin(ang);
    const d = dot(axis, v);
    const x = cross(axis, v);
    return [
      v[0] * c + x[0] * sn + axis[0] * d * (1 - c),
      v[1] * c + x[1] * sn + axis[1] * d * (1 - c),
      v[2] * c + x[2] * sn + axis[2] * d * (1 - c),
    ];
  };
  // Pose : la face de normale (1, 1, 1) — la 20 — vers l'œil (+z), puis
  // tournée dans le plan, inclinée vers l'arrière et tournée de trois quarts.
  const front = unit([1, 1, 1]);
  const toFront = unit(cross(front, [0, 0, 1]));
  const deg = Math.PI / 180;
  const pose = (v: V): V =>
    rot(rot(rot(rot(v, toFront, Math.acos(front[2])), [0, 0, 1], D20_SPIN * deg), [1, 0, 0], -D20_TILT * deg), [0, 1, 0], D20_TURN * deg);
  const posed = verts.map(pose);
  const k = 45 / Math.max(...posed.map((v) => Math.hypot(v[0], v[1])));
  const to2d = (v: V): Pt => [50 + v[0] * k, 51 - v[1] * k];
  const light = unit([-0.55, 0.7, 0.55]);
  const frontIdx = tris.findIndex((t) => dot(unit(t.reduce<V>((c, i) => [c[0] + verts[i][0], c[1] + verts[i][1], c[2] + verts[i][2]], [0, 0, 0])), front) > 0.99);
  const neighbours = tris
    .map((t, i) => ({ i, shared: t.filter((v) => tris[frontIdx].includes(v)).length }))
    .filter((n) => n.shared === 2)
    .map((n) => n.i);
  const labels = new Map<number, string>([[frontIdx, '20']]);
  ['2', '8', '14'].forEach((txt, n) => labels.set(neighbours[n], txt));
  const faces: { face: D20Face; depth: number }[] = [];
  tris.forEach((t, i) => {
    const p = t.map((v) => posed[v]);
    let n = unit(cross(sub(p[1], p[0]), sub(p[2], p[0])));
    const c: V = [(p[0][0] + p[1][0] + p[2][0]) / 3, (p[0][1] + p[1][1] + p[2][1]) / 3, (p[0][2] + p[1][2] + p[2][2]) / 3];
    if (dot(n, c) < 0) n = [-n[0], -n[1], -n[2]];
    if (n[2] <= 1e-6) return;
    const lum = dot(n, light);
    const label = labels.get(i) ?? null;
    const at = to2d(c);
    faces.push({
      face: {
        pts: p.map(to2d),
        shade: lum > 0.8 ? 0 : lum > 0.5 ? 1 : lum > 0.2 ? 2 : 3,
        label,
        at: [at[0], at[1] + 0.5],
        // Les chiffres des faces fuyantes rapetissent avec elles.
        fs: label === '20' ? 14 : 4 + 6 * n[2],
      },
      depth: c[2],
    });
  });
  d20Cache = faces.sort((a, b) => a.depth - b.depth).map((f) => f.face);
  return d20Cache;
}

function d20(edge: string, sw: number): string {
  let g = '';
  for (const f of d20Faces()) g += face(f.pts, BONE[f.shade], edge, sw * 0.85, f.label ?? undefined, f.at, f.fs);
  return g;
}

/**
 * **d100** (v0.43.0, A89) : une **boule** à facettes vue de trois quarts. Même
 * recette d'arêtes que les autres vignettes (liseré de rareté, os ombré), mais
 * le contour est un cercle : une pastille pentagonale « 100 » légèrement
 * décalée vers le haut-gauche, cinq facettes qui l'entourent, puis dix
 * facettes de bord qui épousent le cercle (arcs). Seize facettes en tout :
 * lisible dès 24 px, où seule la silhouette ronde et les liserés se devinent.
 */
function d100(edge: string, sw: number): string {
  const cx = 50;
  const cy = 51;
  const R = 44;
  const hx = cx - 4;
  const hy = cy - 4;
  const P: Pt[] = [];
  const Q: Pt[] = [];
  const S: Pt[] = [];
  const O: Pt[] = [];
  for (let i = 0; i < 5; i++) {
    const a = ((-90 + 72 * i) * Math.PI) / 180;
    const b = ((-90 + 72 * i + 36) * Math.PI) / 180;
    P.push([hx + 16 * Math.cos(a), hy + 16 * Math.sin(a)]);
    Q.push([hx + 29 * Math.cos(a), hy + 29 * Math.sin(a)]);
    S.push([cx + R * Math.cos(b), cy + R * Math.sin(b)]);
    O.push([cx + R * Math.cos(a), cy + R * Math.sin(a)]);
  }
  // Ton d'une facette d'après la direction de son centre : lumière en haut à gauche.
  const toneAt = (x: number, y: number): string => {
    const dx = x - hx;
    const dy = y - hy;
    const l = Math.hypot(dx, dy) || 1;
    const lum = (-dx * 0.7 - dy * 0.7) / l;
    return lum > 0.55 ? BONE[1] : lum > -0.1 ? BONE[2] : BONE[3];
  };
  const f1 = (n: number): string => n.toFixed(1);
  const arc = (to: Pt): string => `A ${R} ${R} 0 0 1 ${f1(to[0])} ${f1(to[1])}`;
  const rim = (d: string, mx: number, my: number): string =>
    `<path d="${d} Z" fill="${toneAt(mx, my)}" stroke="${edge}" stroke-width="${sw * 0.8}" stroke-linejoin="round"/>`;
  let g = '';
  // Facettes de bord : chaque secteur (Q_i, S_prev, O_i, S_i) est coupé en deux par Q_i–O_i.
  for (let i = 0; i < 5; i++) {
    const j = (i + 4) % 5;
    const q = Q[i];
    const o = O[i];
    g += rim(`M ${f1(q[0])} ${f1(q[1])} L ${f1(S[j][0])} ${f1(S[j][1])} ${arc(o)}`, (q[0] + S[j][0] + o[0]) / 3, (q[1] + S[j][1] + o[1]) / 3);
    g += rim(`M ${f1(q[0])} ${f1(q[1])} L ${f1(o[0])} ${f1(o[1])} ${arc(S[i])}`, (q[0] + o[0] + S[i][0]) / 3, (q[1] + o[1] + S[i][1]) / 3);
  }
  // Les cinq facettes qui entourent la pastille centrale.
  for (let i = 0; i < 5; i++) {
    const k = (i + 1) % 5;
    const mx = (P[i][0] + P[k][0] + Q[i][0] + Q[k][0] + S[i][0]) / 5;
    const my = (P[i][1] + P[k][1] + Q[i][1] + Q[k][1] + S[i][1]) / 5;
    g += face([P[i], P[k], Q[k], S[i], Q[i]], toneAt(mx, my), edge, sw);
  }
  g += face(P, BONE[0], edge, sw, '100', [hx, hy + 0.5], 11);
  return g;
}

/** Repli : polygone régulier à `n` côtés (borné à 3–10), face plate numérotée. */
function generic(sides: number, edge: string, sw: number): string {
  const n = Math.max(3, Math.min(10, Math.round(sides) || 6));
  const outer: Pt[] = [];
  const inner: Pt[] = [];
  for (let i = 0; i < n; i++) {
    const a = ((-90 + (360 / n) * i) * Math.PI) / 180;
    outer.push([50 + 44 * Math.cos(a), 51 + 44 * Math.sin(a)]);
    inner.push([50 + 26 * Math.cos(a), 51 + 26 * Math.sin(a)]);
  }
  let g = '';
  for (let i = 0; i < n; i++) {
    const k = (i + 1) % n;
    g += face([outer[i], outer[k], inner[k], inner[i]], i % 2 ? BONE[2] : BONE[3], edge, sw * 0.8);
  }
  g += face(inner, BONE[0], edge, sw, String(sides), [50, 52], 18);
  return g;
}

/**
 * Vignette SVG d'un dé. Décorative (`aria-hidden`) : le nom et la rareté sont
 * écrits à côté.
 */
export function dieArt(sides: number, rarity: Rarity | null, opts: DieArtOptions = {}): string {
  const size = opts.size ?? 48;
  const sw = opts.stroke ?? 2.6;
  let defs = '';
  let edge: string;
  if (opts.locked) edge = LOCKED_EDGE;
  else if (opts.plain || !rarity) edge = PLAIN_EDGE;
  else if (rarity === 'prismatic') {
    gradientSeq = (gradientSeq + 1) % 1_000_000;
    const id = `dod-prism-${gradientSeq}`;
    defs =
      `<defs><linearGradient id="${id}" x1="0" y1="0" x2="1" y2="1">` +
      '<stop offset="0" stop-color="#5ff0ff"/><stop offset=".3" stop-color="#9b7bff"/>' +
      '<stop offset=".55" stop-color="#ff7ad9"/><stop offset=".8" stop-color="#ffd66e"/>' +
      '<stop offset="1" stop-color="#7dffb0"/></linearGradient></defs>';
    edge = `url(#${id})`;
  } else edge = DIE_EDGE[rarity] ?? DIE_EDGE.common;

  const body =
    sides === 4
      ? d4(edge, sw)
      : sides === 6
        ? d6(edge, sw)
        : sides === 12
          ? d12(edge, sw)
          : sides === 20
            ? d20(edge, sw)
            : sides === 100
              ? d100(edge, sw)
              : generic(sides, edge, sw);
  const cls = ['die-art', `die-art--d${Math.round(sides) || 0}`, opts.locked ? 'die-art--locked' : '', opts.className ?? '']
    .filter(Boolean)
    .join(' ')
    .replace(/[^\w\s-]/g, '');
  return (
    `<svg class="${cls}" viewBox="0 0 100 100" width="${size}" height="${size}" aria-hidden="true" focusable="false">` +
    `${defs}${body}</svg>`
  );
}
